import type { CatalogCard } from "@pokifolio/contracts";
import { useId, useState } from "react";

import { ErrorAlert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { CardLookupResults } from "../search/card-lookup-results";
import { useCardLookup } from "../search/search-queries";

interface CardChooserProps {
  readonly listingTitle: string;
  /** Starts with the words that were searched, so the right card is usually one click away. */
  readonly initialText: string;
  readonly busy: boolean;
  readonly error: unknown;
  readonly onChoose: (card: CatalogCard) => void;
  readonly onCancel: () => void;
}

/** A listing found by typed words is saved only once the exact card is chosen (D-50). */
export function CardChooser({
  listingTitle,
  initialText,
  busy,
  error,
  onChoose,
  onCancel,
}: CardChooserProps) {
  const [text, setText] = useState(initialText);
  const lookup = useCardLookup(text);
  const headingId = useId();
  const inputId = useId();

  return (
    <div role="group" aria-labelledby={headingId} className="flex flex-col gap-3">
      <div>
        <p id={headingId} className="text-sm font-semibold text-foreground">
          Which card is this listing?
        </p>
        <p className="text-xs text-muted-foreground">
          “{listingTitle}”. Choose the exact card before saving, so the right card is researched.
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={inputId} className="text-xs font-medium text-muted-foreground">
          Card name and number
        </label>
        <Input
          id={inputId}
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoComplete="off"
          className="max-w-md"
        />
      </div>
      <CardLookupResults
        lookup={lookup}
        busy={busy}
        actionLabel={(card) => `Save as ${card.name} ${card.number}, ${card.setName}`}
        onChoose={onChoose}
        emptyHint="Check the spelling or add the card number."
      />
      {error !== null && error !== undefined && <ErrorAlert error={error} />}
      <div>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
